<template>
  <a-menu mode="inline"
          class="ii-console-menu"
          @select='onSelect'
          @openChange='onOpenChange'
          :selectedKeys='treeState.selectedKeys'
          :openKeys='treeState.openKeys'
          :class="collapsed ? 'ii-console-menu-collapsed' : ''"
          :inlineCollapsed="collapsed">
    <template v-for="item in menus">
      <a-menu-item v-if="!item.children || item.children.length <= 0"
                   :key="item.id">
        <router-link :to='item.linkTo'
                     class="menu"
                     v-if="item.linkTo">
          <auto-icon :type='item.icon || "folder"' />
          <span class="ii-menu-item">{{ item.name }}</span>
        </router-link>
        <div v-else>
          <auto-icon :type='item.icon || "folder"' />
          <span>{{ item.name }}</span>
        </div>
      </a-menu-item>
      <sub-menu v-else
                :menus="item"
                :collapsed='collapsed'
                :key="item.id + 'else'" />
    </template>
  </a-menu>
</template>

<script setup lang="ts">
import { useRbacStore, type ConsolePageOption, type ConsolePageService, type Plugin } from '@/plugins'
import { PLUGIN_CONSOLE_PAGE } from '@/plugins/builtin/BuiltinPluginSymbol'
import { AutoIcon } from '@iota-cn/ui-components'
import { container } from 'tsyringe'
import { computed, onMounted, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import SubMenu from './SubMenu.vue'
const plugin =
  container.resolve<Plugin<ConsolePageOption, ConsolePageService>>(
    PLUGIN_CONSOLE_PAGE
  )

const collapsed = plugin.service.isCollapsed()
const treeState = reactive({
  openKeys: [] as string[],
  selectedKeys: [] as string[]
})
const route = useRoute()
const rbacStore = useRbacStore()

onMounted(() => {
  rbacStore.fetchMenus().then(updateMenuState)
})

const updateMenuState = (): void => {
  const path = route.path
  if (path != '') {
    const { idMap, pathMap } = rbacStore.menus
    const menu = pathMap[path]
    if (menu) {
      const keys = [menu.id]
      let parent = idMap[menu.parentId || '']
      while (parent && parent.id) { //
        keys.push(parent.id)
        parent = idMap[parent.parentId || '']
      }

      treeState.openKeys = treeState.selectedKeys = keys
      if (collapsed.value) {
        treeState.openKeys = []
      }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onSelect = ({ selectedKeys: keys }: { selectedKeys: any }): void => {
  treeState.selectedKeys = keys
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onOpenChange = (keys: any[]): void => {
  treeState.openKeys = keys
}

const menus = computed(() => {
  const tree = rbacStore.menus.tree
  const menuType = rbacStore.menus.menuType
  const targetMenu = tree.find(item => item.key === menuType)
  return targetMenu ? targetMenu.children : []
})

watch(
  () => route.path,
  updateMenuState
)
</script>

<style lang="less" scoped>
.ii-console-menu {
  width: 100%;
  border: none;

  :deep(.ant-menu-inline .ant-menu-item::after) {
    border: none;
  }

  :deep(.ant-menu-item a:hover) {
    color: #0042EA;
  }

  :deep(.ant-menu-submenu-selected) {
    color: #121929;

    >.ant-menu-submenu-title {
      .ii-menu {
        font-weight: bold;
      }
    }
  }

  :deep(.ant-menu-submenu-title:hover) {
    color: #0042EA;
  }

  :deep(.ant-menu-item:active) {
    background: #F2F3FA !important;
  }

  :deep(.ant-menu-submenu-title:active) {
    background: #F2F3FA !important;
  }

  :deep(.ant-menu-submenu:hover>.ant-menu-submenu-title>.ant-menu-submenu-arrow) {
    color: #0042EA;
  }

  :deep(.ant-menu-item-selected) {
    background: #F2F3FA;

    a {
      color: #0042EA;
    }

    a::before {
      width: 4px;
      height: 20px;
      background: #0042EA;
      border-radius: 2px;
      top: 10px;
      left: 2px;
    }
  }

  .ant-menu-item {
    font-size: 14px;
  }

  .menu {
    font-size: 16px;
    font-weight: 500;


  }

  &-collapsed {

    >.ant-menu-item .iota-icon,
    .ant-menu-submenu>.ant-menu-submenu-title .iota-icon {
      +span {
        display: inline-block;
        max-width: 0;
        opacity: 0;
      }
    }
  }
}
</style>
